import React, { useRef, useEffect, useState } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS } from 'chart.js/auto';
import { FaDownload } from 'react-icons/fa';

const DataVisualizer = ({ model, simulationData }) => {
  const [activeTab, setActiveTab] = useState('torqueSpeed');
  const chartRef = useRef(null);
  
  // Store historical data for time-series charts
  const [historicalData, setHistoricalData] = useState({
    timestamps: [],
    slip: [],
    efficiency: [],
    rotorPower: [],
    recoveredPower: [],
    rotorVoltage: [],
    rotorCurrent: []
  });
  
  // Update historical data when simulation data changes
  useEffect(() => {
    if (simulationData) {
      setHistoricalData(prev => {
        const newData = { ...prev };
        const now = Date.now();
        
        // Limit data points to prevent memory issues
        const maxDataPoints = 100;
        
        newData.timestamps = [...prev.timestamps, now].slice(-maxDataPoints);
        newData.slip = [...prev.slip, simulationData.slip * 100].slice(-maxDataPoints);
        newData.efficiency = [...prev.efficiency, simulationData.efficiency * 100].slice(-maxDataPoints);
        newData.rotorPower = [...prev.rotorPower, simulationData.rotorPower / 1000].slice(-maxDataPoints);
        newData.recoveredPower = [...prev.recoveredPower, simulationData.recoveredPower / 1000].slice(-maxDataPoints);
        newData.rotorVoltage = [...prev.rotorVoltage, simulationData.rotorVoltage].slice(-maxDataPoints);
        newData.rotorCurrent = [...prev.rotorCurrent, simulationData.rotorCurrent].slice(-maxDataPoints);
        
        return newData;
      });
    }
  }, [simulationData]);
  
  // Generate torque-speed curve data
  const getTorqueSpeedData = () => {
    const speeds = [];
    const torques = [];
    
    // Generate points for the curve
    for (let speed = 0; speed <= model.syncSpeed; speed += model.syncSpeed / 20) {
      speeds.push(speed);
      
      // Calculate torque based on simplified induction motor model
      // This is a simplified approximation
      const slip = (model.syncSpeed - speed) / model.syncSpeed;
      let torque;
      
      if (model.driveType === 'scherbius') {
        // Scherbius drive extends speed range below synchronous speed
        torque = model.ratedPower * 9.55 / model.syncSpeed * (slip / (0.05 + slip));
      } else { // Kramer
        // Kramer drive has different torque characteristics
        torque = model.ratedPower * 9.55 / model.syncSpeed * (slip / (0.08 + slip));
      }
      
      torques.push(torque);
    }
    
    return {
      labels: speeds,
      datasets: [
        {
          label: 'Torque (Nm)',
          data: torques,
          borderColor: '#ff6384',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          tension: 0.4
        }
      ]
    };
  };
  
  // Generate slip vs efficiency data
  const getSlipEfficiencyData = () => {
    return {
      labels: historicalData.slip,
      datasets: [
        {
          label: 'Efficiency (%)',
          data: historicalData.efficiency,
          borderColor: '#36a2eb',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          yAxisID: 'y'
        },
        {
          label: 'Slip (%)',
          data: historicalData.slip,
          borderColor: '#ff6384',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          yAxisID: 'y'
        }
      ]
    };
  };
  
  // Generate power flow data
  const getPowerFlowData = () => {
    return {
      labels: historicalData.timestamps.map(t => new Date(t).toLocaleTimeString()),
      datasets: [
        {
          label: 'Rotor Power (kW)',
          data: historicalData.rotorPower,
          borderColor: '#ff6384',
          backgroundColor: 'rgba(255, 99, 132, 0.2)'
        },
        {
          label: 'Recovered Power (kW)',
          data: historicalData.recoveredPower,
          borderColor: '#4bc0c0',
          backgroundColor: 'rgba(75, 192, 192, 0.2)'
        }
      ]
    };
  };
  
  // Generate rotor electrical data
  const getRotorElectricalData = () => {
    return {
      labels: historicalData.timestamps.map(t => new Date(t).toLocaleTimeString()),
      datasets: [
        {
          label: 'Rotor Voltage (V)',
          data: historicalData.rotorVoltage,
          borderColor: '#ff6384',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          yAxisID: 'y'
        },
        {
          label: 'Rotor Current (A)',
          data: historicalData.rotorCurrent,
          borderColor: '#36a2eb',
          backgroundColor: 'rgba(54, 162, 235, 0.2)',
          yAxisID: 'y1'
        }
      ]
    };
  };
  
  // Chart options
  const getChartOptions = (chartType) => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 300
      },
      plugins: {
        legend: {
          position: 'top',
        },
        tooltip: {
          mode: 'index',
          intersect: false,
        }
      }
    };
    
    // Add specific options based on chart type
    switch (chartType) {
      case 'torqueSpeed':
        return {
          ...baseOptions,
          scales: {
            x: {
              title: {
                display: true,
                text: 'Speed (RPM)'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Torque (Nm)'
              }
            }
          }
        };
      case 'slipEfficiency':
        return {
          ...baseOptions,
          scales: {
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: {
                display: true,
                text: 'Efficiency (%)'
              },
              min: 0,
              max: 100
            }
          }
        };
      case 'powerFlow':
        return {
          ...baseOptions,
          scales: {
            y: {
              title: {
                display: true,
                text: 'Power (kW)'
              }
            }
          }
        };
      case 'rotorElectrical':
        return {
          ...baseOptions,
          scales: {
            y: {
              type: 'linear',
              display: true,
              position: 'left',
              title: {
                display: true,
                text: 'Voltage (V)'
              }
            },
            y1: {
              type: 'linear',
              display: true,
              position: 'right',
              title: {
                display: true,
                text: 'Current (A)'
              },
              grid: {
                drawOnChartArea: false
              }
            }
          }
        };
      default:
        return baseOptions;
    }
  };
  
  // Get chart data based on active tab
  const getChartData = () => {
    switch (activeTab) {
      case 'torqueSpeed':
        return getTorqueSpeedData();
      case 'slipEfficiency':
        return getSlipEfficiencyData();
      case 'powerFlow':
        return getPowerFlowData();
      case 'rotorElectrical':
        return getRotorElectricalData();
      default:
        return getTorqueSpeedData();
    }
  };
  
  // Export data as CSV
  const exportData = () => {
    const headers = ['Timestamp', 'Slip (%)', 'Efficiency (%)', 'Rotor Power (kW)', 
                    'Recovered Power (kW)', 'Rotor Voltage (V)', 'Rotor Current (A)'];
    
    const rows = historicalData.timestamps.map((timestamp, index) => [
      new Date(timestamp).toLocaleString(),
      historicalData.slip[index].toFixed(2),
      historicalData.efficiency[index].toFixed(2),
      historicalData.rotorPower[index].toFixed(2),
      historicalData.recoveredPower[index].toFixed(2),
      historicalData.rotorVoltage[index].toFixed(2),
      historicalData.rotorCurrent[index].toFixed(2)
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `slip_power_recovery_data_${new Date().toISOString()}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  return (
    <div className="data-visualizer glass-panel">
      <div className="panel-header">
        <h2>Data Visualization</h2>
        <button className="export-btn" onClick={exportData}>
          <FaDownload /> Export Data
        </button>
      </div>
      
      <div className="tabs">
        <button 
          className={activeTab === 'torqueSpeed' ? 'active' : ''}
          onClick={() => setActiveTab('torqueSpeed')}
        >
          Torque-Speed Curve
        </button>
        <button 
          className={activeTab === 'slipEfficiency' ? 'active' : ''}
          onClick={() => setActiveTab('slipEfficiency')}
        >
          Slip vs Efficiency
        </button>
        <button 
          className={activeTab === 'powerFlow' ? 'active' : ''}
          onClick={() => setActiveTab('powerFlow')}
        >
          Power Flow
        </button>
        <button 
          className={activeTab === 'rotorElectrical' ? 'active' : ''}
          onClick={() => setActiveTab('rotorElectrical')}
        >
          Rotor Electrical
        </button>
      </div>
      
      <div className="chart-container">
        <Line 
          ref={chartRef}
          data={getChartData()} 
          options={getChartOptions(activeTab)} 
        />
      </div>
      
      <div className="gauges">
        <div className="gauge">
          <h3>Slip</h3>
          <div className="gauge-value">{(model.slip * 100).toFixed(1)}%</div>
        </div>
        <div className="gauge">
          <h3>Efficiency</h3>
          <div className="gauge-value">{(model.efficiency * 100).toFixed(1)}%</div>
        </div>
        <div className="gauge">
          <h3>Recovered Power</h3>
          <div className="gauge-value">{(model.recoveredPower / 1000).toFixed(2)} kW</div>
        </div>
      </div>
    </div>
  );
};

export default DataVisualizer;